/**
 * Penelope's one action row (StreamRift mode, 2026-10-20): the same buttons in the same order on every Penelope screen
 * — the ticket, the /live popover and the Desk drawer — in the same slot (under the status, above the settings):
 *
 *   [stop | start | start (live)]  [go live | back to practice]  ……  [kill]  [remove]  [more ▾]
 *
 * Each slot renders only when the caller hands it an action (the relay's rule of who may do what is the caller's to
 * mirror). The texts, titles and confirm strings are the ticket's own, so a trader reads the same words everywhere;
 * the popover keeps its long mode labels. `more` folds the rarer links (the timeline CSV, the downloads, the desk) into
 * a second line a click away. Display only: every click goes through the caller's own handler.
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/infra/utils';

export const GO_LIVE_CONFIRM = 'Switch Penelope to LIVE for this game? She will place REAL post-only orders on Kalshi with these settings.';
export const PRACTICE_CONFIRM = 'Back to practice? Her resting Kalshi orders are cancelled and she simulates from here.';
export const KILL_CONFIRM = 'Kill Penelope on this game? This stops the game for good.';
export const REMOVE_CONFIRM = 'Remove Penelope from this game? Her resting orders are cancelled; her inventory stays yours.';

export interface PenelopeActionBarProps {
  busy: boolean;
  armed: boolean;
  isLive: boolean;
  /** Start / Stop. `liveStartLabel`: a stopped LIVE game starts as "start (live)" (the ticket's wording). */
  arm?: { set: (on: boolean) => void; liveStartLabel?: boolean } | null;
  /** Go live / back to practice. `reason` is the title of a locked go-live (the caller's own words); `confirmBack`
   *  asks PRACTICE_CONFIRM before leaving live; `goLiveConfirm` replaces the standard question (the ticket's longer one). */
  mode?: { canGoLive: boolean; reason: string | null; set: (m: 'live' | 'shadow') => void; labels: 'short' | 'long'; confirmBack: boolean; goLiveConfirm?: string } | null;
  /** Kill: asks KILL_CONFIRM first. */
  kill?: (() => void) | null;
  /** Remove: asks REMOVE_CONFIRM first. */
  remove?: (() => void) | null;
  /** The rarer actions behind "more ▾": a button (`onClick`) or a link (`to`). */
  more?: Array<{ label: string; title?: string; onClick?: () => void; to?: string }>;
  children?: ReactNode;
}

const small = 'rounded border px-2 py-1 text-[10px] font-bold uppercase tracking-wide disabled:opacity-50';

export function PenelopeActionBar({ busy, armed, isLive, arm, mode, kill, remove, more = [], children }: PenelopeActionBarProps) {
  const [showMore, setShowMore] = useState(false);
  const locked = !isLive && mode !== null && mode !== undefined && !mode.canGoLive;
  return (
    <div role="group" aria-label="actions" className="flex flex-wrap items-center gap-1.5">
      {arm && (
        <button
          type="button"
          disabled={busy}
          onClick={() => arm.set(!armed)}
          className={cn(small, armed ? 'border-border text-foreground' : isLive ? 'border-red-500 bg-red-500/15 text-red-500' : 'border-sky-500 bg-sky-500/15 text-sky-500')}
          title={armed ? 'Stop quoting on this game (cancels her orders, keeps her inventory)' : isLive ? 'Start: she will place REAL post-only orders on Kalshi' : 'Start in practice: simulated, never writes to Kalshi'}
        >
          {armed ? 'stop' : isLive && arm.liveStartLabel ? 'start (live)' : 'start'}
        </button>
      )}
      {mode && (
        <button
          type="button"
          disabled={busy || locked}
          onClick={() => {
            if (isLive) { if (!mode.confirmBack || window.confirm(PRACTICE_CONFIRM)) mode.set('shadow'); }
            else if (window.confirm(mode.goLiveConfirm ?? GO_LIVE_CONFIRM)) mode.set('live');
          }}
          className={cn(small, isLive ? 'border-border text-muted-foreground hover:text-foreground' : 'border-red-500 text-red-500', locked && 'opacity-50')}
          title={isLive
            ? (mode.confirmBack ? 'Back to practice (asks first)' : 'Back to practice: cancels her resting orders and simulates from here')
            : mode.canGoLive ? 'Go live (asks first)' : (mode.reason ?? 'practice only')}
        >
          {mode.labels === 'long' ? (isLive ? 'live — back to practice' : 'practice — go live') : (isLive ? 'back to practice' : 'go live')}
        </button>
      )}
      <span className="flex-1" />
      {kill && (
        <button type="button" disabled={busy} onClick={() => { if (window.confirm(KILL_CONFIRM)) kill(); }} className={cn(small, 'border-red-500 text-red-500')} title="Stop this game for good">
          kill
        </button>
      )}
      {remove && (
        <button type="button" disabled={busy} onClick={() => { if (window.confirm(REMOVE_CONFIRM)) remove(); }} className="text-muted-foreground hover:text-foreground text-[10px] uppercase" title="Remove Penelope from this game (asks first)">
          remove
        </button>
      )}
      {children}
      {more.length > 0 && (
        <button type="button" aria-expanded={showMore} onClick={() => setShowMore((x) => !x)} className="text-muted-foreground hover:text-foreground text-[10px] uppercase" title="The timeline, the downloads, the desk">
          more {showMore ? '▴' : '▾'}
        </button>
      )}
      {showMore && more.length > 0 && (
        <div className="flex w-full flex-wrap items-center gap-2 text-[10px]">
          {more.map((m) => (m.to
            ? <Link key={m.label} to={m.to} title={m.title} className="text-primary underline">{m.label}</Link>
            : <button key={m.label} type="button" disabled={busy} onClick={m.onClick} title={m.title} className={small}>{m.label}</button>))}
        </div>
      )}
    </div>
  );
}
